import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { notificationsApi } from '../api/notifications-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { DeviceSummaryTab } from '../features/notifications/DeviceSummaryTab';
import { SnoozesTab } from '../features/notifications/SnoozesTab';
import { SummarySettingsTab } from '../features/notifications/SummarySettingsTab';
import { toMap } from '../lib/collections';

const tabs = ['summary', 'snoozed', 'settings'] as const;
type Tab = (typeof tabs)[number];
const tabLabels: Record<Tab, string> = {
  summary: 'This week',
  snoozed: 'Snoozed',
  settings: 'Settings',
};

/**
 * Notifications (tidaltech/koi#8361): the weekly summary of devices at
 * customer sites, the devices snoozed out of it, and its settings.
 */
export function NotificationsPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'summary';
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const settings = useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: () => notificationsApi.listSettings(),
  });
  const sitesById = useMemo(() => toMap(sites.data ?? []), [sites.data]);
  const customersById = useMemo(() => toMap(customers.data ?? []), [customers.data]);

  return (
    <>
      <PageHeader
        title="Notifications"
        description="Who is told about Tidal devices at customer sites that went quiet or weren’t installed. Emails go to our own owners of a customer once its watch-only date has passed."
      />
      <div className="detail-tabs" role="tablist" aria-label="Notification sections">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={tab === t ? 'active' : undefined}
            onClick={() => setParams(t === 'summary' ? {} : { tab: t })}
          >
            {tabLabels[t]}
          </button>
        ))}
      </div>
      {tab === 'summary' ? (
        <DeviceSummaryTab
          sitesById={sitesById}
          customersById={customersById}
          snoozeMaxDays={settings.data?.defaults.snoozeMaxDays ?? 90}
        />
      ) : null}
      {tab === 'snoozed' ? <SnoozesTab sitesById={sitesById} /> : null}
      {tab === 'settings' ? (
        settings.isLoading ? (
          <PageLoadingState label="Loading settings" className="section-loading-state" />
        ) : settings.isError || !settings.data ? (
          <ErrorState
            title="Settings could not be loaded"
            rpcName="ListNotificationSettings"
            onRetry={() => void settings.refetch()}
          />
        ) : (
          <SummarySettingsTab settings={settings.data} customersById={customersById} />
        )
      ) : null}
    </>
  );
}
